import { createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { ArticleDetailScreen } from '@/common/ArticleDetailScreen';
import { NotFoundScreen } from '@/common/ErrorScreen';

/**
 * `single-follow_up_tut.php:27` falls through to `template-parts/content.php`
 * because no `content-follow_up_tut.php` exists — so the page is title, date
 * and body and nothing else. `:42` wraps the sidebar in
 * `if (!is_single() || is_related_posts())`, false on a singular, and the
 * crawled response carries no `id="secondary"`, no `id="comments"`, no author
 * box, no related posts and no category chips.
 *
 * DIV-16 — the back arrow lands on the unfiltered archive; legacy's control is
 * `<a onclick='history.back();'>` (`template-parts/content.php:107`), so a
 * reader who arrived from `/follow-up-tutorials/?search_term=partner` returns
 * to that filtered list. `ArticleDetailScreen.tsx:28-30` navigates to a fixed
 * `backPath`. Not fixed here: that component is shared with `news.$slug.tsx`
 * and `study-helper-tutorials.$slug.tsx`, both Wave-5 rows, and no control on
 * this surface emits `search_term` in the first place (DIV-8). It also runs the
 * other way — `history.back()` on a deep-linked detail leaves the site.
 */
function FollowUpTutorialDetail(): ReactElement | null {
  const { t } = useTranslation();
  const { slug } = Route.useParams();
  const { data, isError, isPending } = api.getFollowUpTutorial.useQuery({
    path: { slug },
  });

  // Legacy 404s server-side, so a refusal is the page-not-found SCREEN rather
  // than the inline box. `isError` is deliberately not narrowed to 404: both
  // shipped follow-up detail routes branch on bare `isError`
  // (`follow-ups.entries.view.$followUpId.tsx:129`,
  // `follow-ups.pending-requests.view.$followUpId.tsx:120`), so a 5xx renders
  // as page-not-found too. Shipped convention; accepted, not fixed here.
  if (isError) return <NotFoundScreen />;
  // NOT optional, and no test can hold it: `ArticleDetailScreen.tsx:32-40`
  // renders `notFoundMessage` whenever `article` is falsy, and `data` is
  // undefined for the whole in-flight window — so handing it `data` unguarded
  // flashes "Page not found" on every first paint of every SUCCESSFUL load.
  // Playwright auto-waits past the flash.
  if (isPending || data === undefined) return null;

  return (
    <ArticleDetailScreen
      article={{
        title: data.title,
        content: data.content,
        // The rendered `F j, Y` string (`wp_options.date_format`), e.g.
        // "December 2, 2022".
        date: data.published_on,
      }}
      // NOT the sibling's "/tutorials" (`study-helper-tutorials.$slug.tsx:16`).
      backPath="/follow-up-tutorials"
      // With the two guards above this box is unreachable; the prop is
      // required. `wbs.page-not-found` already ships 4/4, so no "Tutorial not
      // found" string is minted — the two sibling routes hardcode
      // un-internationalized English and this route does not join them.
      notFoundMessage={t('wbs.page-not-found')}
      // `showDate` left at its `true` default. The sibling passes
      // `showDate={false}` and copying it verbatim would drop a date the legacy
      // single really does render (`<span class="post-date">`).
    />
  );
}

export const Route = createFileRoute('/_app/follow-up-tutorials/$slug')({
  component: FollowUpTutorialDetail,
});
